import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RequestService from '../services/requestService';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Divider, 
  Switch, 
  TextField, 
  Button, 
  Avatar, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  ListItemSecondaryAction,
  IconButton,
  Tabs,
  Tab,
  FormControlLabel,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Slider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Palette as PaletteIcon,
  CreditCard as CreditCardIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';

// Beispieldaten für die Einstellungen
const userProfile = {
  name: 'Dr. Michael Weber',
  email: 'michael.weber@klinikum-berlin.de',
  company: 'Universitätsklinikum Berlin',
  role: 'Datenmanager',
  phone: '+49 30 987654321',
  address: 'Klinikstraße 123, 10117 Berlin',
  avatar: null, // Hier könnte ein Bild-URL stehen
  department: 'Medizinische Informatik',
  apiKey: 'sk_test_51NzXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX',
  language: 'de',
  theme: 'light',
  twoFactorEnabled: true
};

const billingInfo = {
  plan: 'Institutional',
  nextBilling: '15.04.2025',
  paymentMethod: 'Rechnung',
  accountNumber: 'KH-2023-0042',
  invoices: [
    { id: 'INV-2025-001', date: '15.01.2025', amount: '1.200,00 €', status: 'bezahlt' },
    { id: 'INV-2024-012', date: '15.12.2024', amount: '1.200,00 €', status: 'bezahlt' },
    { id: 'INV-2024-011', date: '15.11.2024', amount: '1.200,00 €', status: 'bezahlt' }
  ]
};

const organizationSettings = {
  name: 'Universitätsklinikum Berlin',
  type: 'Krankenhaus',
  size: '5000+ Mitarbeiter',
  industry: 'Gesundheitswesen',
  website: 'www.uniklinikum-berlin.de',
  logo: null, // Hier könnte ein Logo-URL stehen
  primaryColor: '#0066CC',
  members: [
    { id: 1, name: 'Dr. Michael Weber', email: 'michael.weber@klinikum-berlin.de', role: 'Admin' },
    { id: 2, name: 'Prof. Dr. Anna Müller', email: 'anna.mueller@klinikum-berlin.de', role: 'Reviewer' },
    { id: 3, name: 'Jan Schmidt', email: 'jan.schmidt@klinikum-berlin.de', role: 'Analyst' }
  ]
};

const notificationSettings = {
  email: {
    newRequests: true,
    statusChanges: true,
    messages: true,
    billing: true,
    weeklyReport: false,
    paymentReceived: true
  },
  inApp: {
    newRequests: true,
    statusChanges: true,
    messages: true,
    billing: true,
    paymentReceived: true
  }
};

const privacySettings = {
  dataSharing: 'minimal',
  anonymizeResults: true,
  allowAnalytics: true,
  retentionPeriod: 36, // Monate
  dataEncryption: 'advanced'
};

const apiSettings = {
  enabled: true,
  rateLimit: 100,
  webhookUrl: 'https://api.uniklinikum-berlin.de/argus-webhook',
  ipWhitelist: ['192.168.1.1', '10.0.0.1'],
  dataExportSettings: {
    allowedFormats: ['csv', 'json', 'xlsx'],
    encryptionRequired: true
  }
};

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showApiKey, setShowApiKey] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(userProfile);
  const [currentBilling, setCurrentBilling] = useState(billingInfo);
  const [currentOrg, setCurrentOrg] = useState(organizationSettings);
  const [currentNotifications, setCurrentNotifications] = useState(notificationSettings);
  const [currentPrivacy, setCurrentPrivacy] = useState(privacySettings);
  const [currentApi, setCurrentApi] = useState(apiSettings);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Test-Funktion für Backend-Verbindung
  const [backendStatus, setBackendStatus] = useState(null);
  
  useEffect(() => {
    const testBackendConnection = async () => {
      try {
        // Verwende den RequestService für den Verbindungstest
        const status = await RequestService.testConnection();
        setBackendStatus(status);
        
        if (!status.connected) {
          console.log('Verwende Fallback-Daten, da keine Backend-Verbindung besteht');
        }
      } catch (error) {
        console.error('Fehler beim Testen der Backend-Verbindung:', error);
        setBackendStatus({
          connected: false,
          error: error.message
        });
      }
    };
    
    // Importiere RequestService
    import('../services/requestService').then(module => {
      window.RequestService = module.default; // Für Debugging
      testBackendConnection();
    }).catch(error => {
      console.error('Fehler beim Importieren des RequestService:', error);
      setBackendStatus({
        connected: false,
        error: 'Fehler beim Laden des RequestService'
      });
    });
  }, []);

  const handleSaveSettings = (section) => {
    setSnackbarMessage(`${section}-Einstellungen wurden erfolgreich gespeichert.`);
    setSnackbarOpen(true);
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  const handleProfileChange = (field, value) => {
    setCurrentProfile({
      ...currentProfile,
      [field]: value
    });
  };
  
  const handleNotificationChange = (category, setting, value) => {
    setCurrentNotifications({
      ...currentNotifications,
      [category]: {
        ...currentNotifications[category],
        [setting]: value
      }
    });
  };
  
  const handlePrivacyChange = (field, value) => {
    setCurrentPrivacy({
      ...currentPrivacy,
      [field]: value
    });
  };
  
  const handleApiChange = (field, value) => {
    setCurrentApi({
      ...currentApi,
      [field]: value
    });
  };
  
  const handleAddMember = () => {
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  const handleDeleteMember = (id) => {
    // In einer realen Anwendung würde hier ein API-Aufruf erfolgen
    const updatedMembers = currentOrg.members.filter(member => member.id !== id);
    setCurrentOrg({
      ...currentOrg,
      members: updatedMembers
    });
    setSnackbarMessage('Teammitglied wurde entfernt.');
    setSnackbarOpen(true);
  };
  
  const handleGenerateNewApiKey = () => {
    // In einer realen Anwendung würde hier ein API-Aufruf erfolgen
    const newApiKey = 'sk_test_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    setCurrentProfile({
      ...currentProfile,
      apiKey: newApiKey
    });
    setSnackbarMessage('Neuer API-Schlüssel wurde generiert.');
    setSnackbarOpen(true);
  };
  
  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
          Einstellungen
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Verwalten Sie Ihre Konto-, Organisations- und Anwendungseinstellungen.
        </Typography>
        
        {backendStatus && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: backendStatus.connected ? 'success.light' : 'error.light' }}>
            <Typography variant="body2" color="text.primary">
              Backend-Status: {backendStatus.connected ? 'Verbunden' : 'Nicht verbunden'}
              {backendStatus.connected && ` - ${backendStatus.message} (${new Date(backendStatus.timestamp).toLocaleTimeString()})`}
              {backendStatus.connected && ` - URL: ${backendStatus.url}`}
              {!backendStatus.connected && ` - Fehler: ${backendStatus.error}`}
            </Typography>
            {!backendStatus.connected && (
              <Button 
                variant="contained" 
                color="primary" 
                size="small" 
                sx={{ mt: 1 }}
                onClick={() => window.location.reload()}
              >
                Erneut versuchen
              </Button>
            )}
          </Paper>
        )}
        
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="settings tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                py: 2
              }
            }}
          >
            <Tab 
              icon={<PersonIcon />} 
              label="Profil" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<BusinessIcon />} 
              label="Organisation" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<NotificationsIcon />} 
              label="Benachrichtigungen" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<SecurityIcon />} 
              label="Datenschutz & Sicherheit" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<CreditCardIcon />} 
              label="Abrechnung" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<LanguageIcon />} 
              label="API & Integrationen" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<PaletteIcon />} 
              label="Darstellung" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
          
          <Box sx={{ p: 3 }}>
            {/* Profil-Einstellungen */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <IconButton 
                            size="small" 
                            sx={{ 
                              bgcolor: 'primary.main', 
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.dark'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <Avatar 
                          sx={{ 
                            width: 120, 
                            height: 120, 
                            mb: 2,
                            fontSize: '3rem',
                            bgcolor: 'primary.main'
                          }}
                        >
                          {currentProfile.name.charAt(0)}
                        </Avatar>
                      </Badge>
                      <Typography variant="h6" gutterBottom>
                        {currentProfile.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {currentProfile.role}
                      </Typography>
                      <Chip 
                        label={currentProfile.company} 
                        color="primary" 
                        variant="outlined" 
                        size="small" 
                        sx={{ mt: 1 }}
                      />
                      
                      <Divider sx={{ width: '100%', my: 2 }} />
                      
                      <List sx={{ width: '100%' }}>
                        <ListItem disableGutters>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <EmailIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary="E-Mail" 
                            secondary={currentProfile.email} 
                          />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <PhoneIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Telefon" 
                            secondary={currentProfile.phone} 
                          />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <LocationOnIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Adresse" 
                            secondary={currentProfile.address} 
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Persönliche Informationen
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Name"
                            value={currentProfile.name}
                            onChange={(e) => handleProfileChange('name', e.target.value)}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="E-Mail"
                            value={currentProfile.email}
                            onChange={(e) => handleProfileChange('email', e.target.value)}
                            variant="outlined"
                            margin="normal"
                            type="email"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Telefon"
                            value={currentProfile.phone}
                            onChange={(e) => handleProfileChange('phone', e.target.value)}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Position/Rolle"
                            value={currentProfile.role}
                            onChange={(e) => handleProfileChange('role', e.target.value)}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Unternehmen"
                            value={currentProfile.company}
                            onChange={(e) => handleProfileChange('company', e.target.value)}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Abteilung"
                            value={currentProfile.department}
                            onChange={(e) => handleProfileChange('department', e.target.value)}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Adresse"
                            value={currentProfile.address}
                            onChange={(e) => handleProfileChange('address', e.target.value)}
                            variant="outlined"
                            margin="normal"
                            multiline
                            rows={2}
                          />
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Sicherheitseinstellungen
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Passwort ändern"
                              type="password"
                              variant="outlined"
                              margin="normal"
                              placeholder="Neues Passwort eingeben"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch 
                                  checked={currentProfile.twoFactorEnabled}
                                  onChange={(e) => handleProfileChange('twoFactorEnabled', e.target.checked)}
                                  color="primary"
                                />
                              }
                              label="Zwei-Faktor-Authentifizierung aktivieren"
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Erhöhen Sie die Sicherheit Ihres Kontos durch eine zusätzliche Verifizierung bei der Anmeldung.
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                      
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveSettings('Profil')}
                        >
                          Änderungen speichern
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            
            {/* Organisations-Einstellungen */}
            {activeTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <IconButton 
                            size="small" 
                            sx={{ 
                              bgcolor: 'primary.main', 
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.dark'
                              }
                            }}
                          >
                            <CloudUploadIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <Avatar 
                          sx={{ 
                            width: 120, 
                            height: 120, 
                            mb: 2,
                            fontSize: '2.5rem',
                            bgcolor: currentOrg.primaryColor || 'primary.main'
                          }}
                        >
                          {currentOrg.name.charAt(0)}
                        </Avatar>
                      </Badge>
                      <Typography variant="h6" gutterBottom>
                        {currentOrg.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {currentOrg.type}
                      </Typography>
                      <Chip 
                        label={currentOrg.industry} 
                        color="primary" 
                        variant="outlined" 
                        size="small" 
                        sx={{ mt: 1 }}
                      />
                      
                      <Divider sx={{ width: '100%', my: 2 }} />
                      
                      <List sx={{ width: '100%' }}>
                        <ListItem disableGutters>
                          <ListItemText 
                            primary="Unternehmensgröße" 
                            secondary={currentOrg.size} 
                          />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText 
                            primary="Website" 
                            secondary={currentOrg.website} 
                          />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText 
                            primary="Primärfarbe" 
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box 
                                  sx={{ 
                                    width: 20, 
                                    height: 20, 
                                    borderRadius: '50%', 
                                    bgcolor: currentOrg.primaryColor 
                                  }} 
                                />
                                {currentOrg.primaryColor}
                              </Box>
                            } 
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Organisationsinformationen
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Organisationsname"
                            value={currentOrg.name}
                            onChange={(e) => setCurrentOrg({...currentOrg, name: e.target.value})}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Organisationstyp"
                            value={currentOrg.type}
                            onChange={(e) => setCurrentOrg({...currentOrg, type: e.target.value})}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth margin="normal">
                            <InputLabel>Unternehmensgröße</InputLabel>
                            <Select
                              value={currentOrg.size}
                              label="Unternehmensgröße"
                              onChange={(e) => setCurrentOrg({...currentOrg, size: e.target.value})}
                            >
                              <MenuItem value="1-50 Mitarbeiter">1-50 Mitarbeiter</MenuItem>
                              <MenuItem value="51-200 Mitarbeiter">51-200 Mitarbeiter</MenuItem>
                              <MenuItem value="201-1000 Mitarbeiter">201-1000 Mitarbeiter</MenuItem>
                              <MenuItem value="1000-5000 Mitarbeiter">1000-5000 Mitarbeiter</MenuItem>
                              <MenuItem value=">5000 Mitarbeiter">&gt;5000 Mitarbeiter</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Branche"
                            value={currentOrg.industry}
                            onChange={(e) => setCurrentOrg({...currentOrg, industry: e.target.value})}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Website"
                            value={currentOrg.website}
                            onChange={(e) => setCurrentOrg({...currentOrg, website: e.target.value})}
                            variant="outlined"
                            margin="normal"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Primärfarbe"
                            value={currentOrg.primaryColor}
                            onChange={(e) => setCurrentOrg({...currentOrg, primaryColor: e.target.value})}
                            variant="outlined"
                            margin="normal"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box 
                                    sx={{ 
                                      width: 20, 
                                      height: 20, 
                                      borderRadius: '50%', 
                                      bgcolor: currentOrg.primaryColor 
                                    }} 
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveSettings('Organisation')}
                        >
                          Änderungen speichern
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                          Teammitglieder
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={handleAddMember}
                        >
                          Mitglied hinzufügen
                        </Button>
                      </Box>
                      <Divider sx={{ mb: 3 }} />
                      
                      <List>
                        {currentOrg.members.map((member) => (
                          <ListItem 
                            key={member.id}
                            secondaryAction={
                              <Box>
                                <Tooltip title="Bearbeiten">
                                  <IconButton edge="end" aria-label="edit" sx={{ mr: 1 }}>
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Entfernen">
                                  <IconButton 
                                    edge="end" 
                                    aria-label="delete"
                                    onClick={() => handleDeleteMember(member.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'primary.light' }}>
                                {member.name.charAt(0)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={member.name}
                              secondary={
                                <React.Fragment>
                                  <Typography component="span" variant="body2" color="text.primary">
                                    {member.email}
                                  </Typography>
                                  {' — '}
                                  <Chip 
                                    label={member.role} 
                                    size="small" 
                                    color={member.role === 'Admin' ? 'primary' : 'default'}
                                    variant={member.role === 'Admin' ? 'filled' : 'outlined'}
                                  />
                                </React.Fragment>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            
            {/* Benachrichtigungs-Einstellungen */}
            {activeTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        E-Mail-Benachrichtigungen
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <List>
                        <ListItem>
                          <ListItemText 
                            primary="Neue Datenanfragen" 
                            secondary="Erhalten Sie eine E-Mail, wenn neue Anfragen eingehen" 
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.email.newRequests}
                              onChange={(e) => handleNotificationChange('email', 'newRequests', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary="Statusänderungen" 
                            secondary="Erhalten Sie eine E-Mail, wenn sich der Status Ihrer Anfragen ändert" 
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.email.statusChanges}
                              onChange={(e) => handleNotificationChange('email', 'statusChanges', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary="Neue Nachrichten" 
                            secondary="Erhalten Sie eine E-Mail, wenn Sie neue Nachrichten erhalten" 
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.email.messages}
                              onChange={(e) => handleNotificationChange('email', 'messages', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary="Abrechnungsinformationen" 
                            secondary="Erhalten Sie eine E-Mail zu Abrechnungen und Zahlungen" 
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.email.billing}
                              onChange={(e) => handleNotificationChange('email', 'billing', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary="Wöchentlicher Bericht" 
                            secondary="Erhalten Sie einen wöchentlichen Bericht über Ihre Anfragen" 
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.email.weeklyReport}
                              onChange={(e) => handleNotificationChange('email', 'weeklyReport', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        In-App-Benachrichtigungen
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <List>
                        <ListItem>
                          <ListItemText 
                            primary="Neue Datenanfragen" 
                            secondary="Erhalten Sie eine Benachrichtigung, wenn neue Anfragen eingehen" 
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.inApp.newRequests}
                              onChange={(e) => handleNotificationChange('inApp', 'newRequests', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary="Statusänderungen" 
                            secondary="Erhalten Sie eine Benachrichtigung, wenn sich der Status Ihrer Anfragen ändert" 
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.inApp.statusChanges}
                              onChange={(e) => handleNotificationChange('inApp', 'statusChanges', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary="Neue Nachrichten" 
                            secondary="Erhalten Sie eine Benachrichtigung, wenn Sie neue Nachrichten erhalten" 
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.inApp.messages}
                              onChange={(e) => handleNotificationChange('inApp', 'messages', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary="Abrechnungsinformationen" 
                            secondary="Erhalten Sie eine Benachrichtigung zu Abrechnungen und Zahlungen" 
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={currentNotifications.inApp.billing}
                              onChange={(e) => handleNotificationChange('inApp', 'billing', e.target.checked)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      </List>
                      
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveSettings('Benachrichtigungen')}
                        >
                          Einstellungen speichern
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            
            {/* Datenschutz & Sicherheit */}
            {activeTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Datenschutzeinstellungen
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Datenfreigabe
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Legen Sie fest, wie viele Informationen über Ihre Anfragen mit Partnerkliniken geteilt werden.
                        </Typography>
                        <Select
                          value={currentPrivacy.dataSharing}
                          onChange={(e) => handlePrivacyChange('dataSharing', e.target.value)}
                          fullWidth
                        >
                          <MenuItem value="minimal">Minimal - Nur notwendige Daten teilen</MenuItem>
                          <MenuItem value="standard">Standard - Grundlegende Informationen teilen</MenuItem>
                          <MenuItem value="extended">Erweitert - Detaillierte Informationen teilen</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={currentPrivacy.anonymizeResults}
                            onChange={(e) => handlePrivacyChange('anonymizeResults', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Ergebnisse anonymisieren"
                        sx={{ display: 'block', mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Wenn aktiviert, werden alle Patientendaten in Analyseergebnissen automatisch anonymisiert.
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={currentPrivacy.allowAnalytics}
                            onChange={(e) => handlePrivacyChange('allowAnalytics', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Nutzungsanalyse zulassen"
                        sx={{ display: 'block', mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Erlauben Sie uns, anonymisierte Nutzungsdaten zu sammeln, um den Service zu verbessern.
                      </Typography>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Aufbewahrungszeitraum für Anfragedaten
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Legen Sie fest, wie lange Ihre Anfragedaten gespeichert werden sollen.
                      </Typography>
                      <Box sx={{ width: '100%', px: 2 }}>
                        <Slider
                          value={currentPrivacy.retentionPeriod}
                          onChange={(e, newValue) => handlePrivacyChange('retentionPeriod', newValue)}
                          aria-labelledby="retention-period-slider"
                          valueLabelDisplay="auto"
                          step={6}
                          marks={[
                            { value: 6, label: '6 Monate' },
                            { value: 12, label: '1 Jahr' },
                            { value: 24, label: '2 Jahre' },
                            { value: 36, label: '3 Jahre' },
                            { value: 60, label: '5 Jahre' }
                          ]}
                          min={6}
                          max={60}
                          valueLabelFormat={(value) => value === 12 ? '1 Jahr' : value < 12 ? `${value} Monate` : `${value / 12} Jahre`}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Sicherheitseinstellungen
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Zwei-Faktor-Authentifizierung
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Switch 
                          checked={currentProfile.twoFactorEnabled}
                          onChange={(e) => handleProfileChange('twoFactorEnabled', e.target.checked)}
                          color="primary"
                        />
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="body1">
                            {currentProfile.twoFactorEnabled ? 'Aktiviert' : 'Deaktiviert'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {currentProfile.twoFactorEnabled 
                              ? 'Ihre Anmeldung ist durch einen zweiten Faktor geschützt.' 
                              : 'Aktivieren Sie diese Option für zusätzliche Sicherheit.'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Passwort ändern
                      </Typography>
                      <TextField
                        fullWidth
                        label="Aktuelles Passwort"
                        type="password"
                        variant="outlined"
                        margin="normal"
                      />
                      <TextField
                        fullWidth
                        label="Neues Passwort"
                        type="password"
                        variant="outlined"
                        margin="normal"
                      />
                      <TextField
                        fullWidth
                        label="Neues Passwort bestätigen"
                        type="password"
                        variant="outlined"
                        margin="normal"
                      />
                      
                      <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>
                        Sitzungsverwaltung
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        Alle anderen Sitzungen beenden
                      </Button>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Beendet alle aktiven Sitzungen außer der aktuellen.
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Datenzugriffsprotokolle
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Typography variant="body2" paragraph>
                        Hier können Sie einsehen, wer auf Ihre Daten zugegriffen hat und wann.
                      </Typography>
                      
                      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                        <ListItem alignItems="flex-start">
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <SecurityIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary="Anmeldung von neuem Gerät"
                            secondary={
                              <React.Fragment>
                                <Typography component="span" variant="body2" color="text.primary">
                                  08.03.2025, 09:45 Uhr
                                </Typography>
                                {" — IP-Adresse: 192.168.1.1, Berlin, Deutschland"}
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                        <Divider variant="inset" component="li" />
                        <ListItem alignItems="flex-start">
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <SecurityIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary="Passwort geändert"
                            secondary={
                              <React.Fragment>
                                <Typography component="span" variant="body2" color="text.primary">
                                  01.03.2025, 14:30 Uhr
                                </Typography>
                                {" — IP-Adresse: 192.168.1.1, Berlin, Deutschland"}
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                        <Divider variant="inset" component="li" />
                        <ListItem alignItems="flex-start">
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              <SecurityIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary="Datenzugriff: Diabetes Typ 2 Studie"
                            secondary={
                              <React.Fragment>
                                <Typography component="span" variant="body2" color="text.primary">
                                  28.02.2025, 11:15 Uhr
                                </Typography>
                                {" — Zugriff durch: Dr. Thomas Weber"}
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                      </List>
                      
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button color="primary">
                          Vollständige Protokolle anzeigen
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={() => handleSaveSettings('Datenschutz')}
                    >
                      Einstellungen speichern
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
            
            {/* Abrechnungs-Einstellungen */}
            {activeTab === 4 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Aktueller Plan
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Chip 
                          label={currentBilling.plan} 
                          color="primary" 
                          sx={{ fontSize: '1.2rem', py: 3, px: 2 }}
                        />
                      </Box>
                      
                      <Typography variant="body1" gutterBottom>
                        <strong>Nächste Abrechnung:</strong> {currentBilling.nextBilling}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        <strong>Zahlungsmethode:</strong> {currentBilling.paymentMethod}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        <strong>Institutionsnummer:</strong> {currentBilling.accountNumber}
                      </Typography>
                      
                      <Box sx={{ mt: 3 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          sx={{ mb: 2 }}
                        >
                          Abrechnungsdetails ändern
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                        >
                          Servicepaket ändern
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Rechnungshistorie
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Rechnungs-ID</TableCell>
                              <TableCell>Datum</TableCell>
                              <TableCell>Betrag</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Aktionen</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentBilling.invoices.map((invoice) => (
                              <TableRow key={invoice.id}>
                                <TableCell>{invoice.id}</TableCell>
                                <TableCell>{invoice.date}</TableCell>
                                <TableCell>{invoice.amount}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={invoice.status} 
                                    color={invoice.status === 'bezahlt' ? 'success' : 'warning'} 
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="text"
                                    color="primary"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                  >
                                    PDF
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Rechnungsadresse
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Unternehmen"
                              value={currentOrg.name}
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Ansprechpartner"
                              value={currentProfile.name}
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Adresse"
                              value={currentProfile.address}
                              variant="outlined"
                              margin="normal"
                              multiline
                              rows={2}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="E-Mail für Rechnungen"
                              value={currentProfile.email}
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Umsatzsteuer-ID"
                              placeholder="DE123456789"
                              variant="outlined"
                              margin="normal"
                            />
                          </Grid>
                        </Grid>
                        
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={() => handleSaveSettings('Abrechnung')}
                          >
                            Änderungen speichern
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            
            {/* API & Integrationen */}
            {activeTab === 5 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        API-Einstellungen
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={currentApi.enabled}
                            onChange={(e) => handleApiChange('enabled', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="API-Zugriff aktivieren"
                        sx={{ display: 'block', mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Ermöglicht den Zugriff auf ARGUS-Daten über die API.
                      </Typography>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        API-Schlüssel
                      </Typography>
                      <TextField
                        fullWidth
                        value={currentProfile.apiKey}
                        variant="outlined"
                        margin="normal"
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle api key visibility"
                                onClick={() => setShowApiKey(!showApiKey)}
                                edge="end"
                              >
                                {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                          type: showApiKey ? 'text' : 'password'
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<RefreshIcon />}
                        onClick={handleGenerateNewApiKey}
                        sx={{ mt: 1 }}
                      >
                        Neuen Schlüssel generieren
                      </Button>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Achtung: Das Generieren eines neuen Schlüssels macht den alten ungültig.
                      </Typography>
                      
                      <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>
                        Rate Limit
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Maximale Anzahl an API-Anfragen pro Stunde.
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={currentApi.rateLimit}
                        onChange={(e) => handleApiChange('rateLimit', parseInt(e.target.value))}
                        variant="outlined"
                        margin="normal"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">Anfragen/Stunde</InputAdornment>,
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Webhook-Konfiguration
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Typography variant="body2" paragraph>
                        Konfigurieren Sie Webhooks, um Benachrichtigungen über Ereignisse zu erhalten.
                      </Typography>
                      
                      <TextField
                        fullWidth
                        label="Webhook-URL"
                        value={currentApi.webhookUrl}
                        onChange={(e) => handleApiChange('webhookUrl', e.target.value)}
                        variant="outlined"
                        margin="normal"
                        placeholder="https://example.com/webhook"
                      />
                      
                      <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>
                        Ereignisse
                      </Typography>
                      <FormControlLabel
                        control={<Switch defaultChecked color="primary" />}
                        label="Neue Analyseergebnisse"
                      />
                      <FormControlLabel
                        control={<Switch defaultChecked color="primary" />}
                        label="Statusänderungen"
                      />
                      <FormControlLabel
                        control={<Switch color="primary" />}
                        label="Neue Nachrichten"
                      />
                      
                      <Box sx={{ mt: 3 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          sx={{ mr: 1 }}
                        >
                          Webhook testen
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveSettings('Webhook')}
                        >
                          Speichern
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        IP-Whitelist
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Typography variant="body2" paragraph>
                        Beschränken Sie den API-Zugriff auf bestimmte IP-Adressen.
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        {currentApi.ipWhitelist.map((ip, index) => (
                          <Chip
                            key={index}
                            label={ip}
                            onDelete={() => {
                              const newList = [...currentApi.ipWhitelist];
                              newList.splice(index, 1);
                              handleApiChange('ipWhitelist', newList);
                            }}
                            sx={{ mr: 1, mb: 1 }}
                          />
                        ))}
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          label="Neue IP-Adresse"
                          variant="outlined"
                          size="small"
                          placeholder="z.B. 192.168.1.1"
                          fullWidth
                        />
                        <Button
                          variant="contained"
                          color="primary"
                        >
                          Hinzufügen
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Integrationen
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>R</Avatar>
                                <Typography variant="h6">
                                  R Studio
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                Verbinden Sie die ARGUS-Platform mit R Studio für automatisierte Datenanalysen.
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                              >
                                Verbinden
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>P</Avatar>
                                <Typography variant="h6">
                                  Python
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                Nutzen Sie Python-Bibliotheken für automatisierte Datenanalysen und Ergebnisaufbereitung.
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                              >
                                Verbinden
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>T</Avatar>
                                <Typography variant="h6">
                                  Tableau
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                Erstellen Sie Dashboards zur Visualisierung von Anfragen und Analysestatistiken.
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                              >
                                Verbinden
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={() => handleSaveSettings('API')}
                    >
                      Alle API-Einstellungen speichern
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
            
            {/* Darstellungs-Einstellungen */}
            {activeTab === 6 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Erscheinungsbild
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Farbschema
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <Button
                          variant={currentProfile.theme === 'light' ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleProfileChange('theme', 'light')}
                          sx={{ flex: 1 }}
                        >
                          Hell
                        </Button>
                        <Button
                          variant={currentProfile.theme === 'dark' ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleProfileChange('theme', 'dark')}
                          sx={{ flex: 1 }}
                        >
                          Dunkel
                        </Button>
                        <Button
                          variant={currentProfile.theme === 'system' ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleProfileChange('theme', 'system')}
                          sx={{ flex: 1 }}
                        >
                          System
                        </Button>
                      </Box>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Primärfarbe
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                        {['#FF7A00', '#00B8D4', '#4CAF50', '#F44336', '#9C27B0', '#2196F3'].map((color) => (
                          <Box
                            key={color}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: color,
                              cursor: 'pointer',
                              border: currentOrg.primaryColor === color ? '2px solid #000' : 'none',
                              '&:hover': {
                                opacity: 0.8
                              }
                            }}
                            onClick={() => setCurrentOrg({...currentOrg, primaryColor: color})}
                          />
                        ))}
                      </Box>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Schriftgröße
                      </Typography>
                      <Box sx={{ width: '100%', px: 2, mb: 3 }}>
                        <Slider
                          defaultValue={1}
                          step={0.1}
                          marks={[
                            { value: 0.8, label: 'Klein' },
                            { value: 1, label: 'Normal' },
                            { value: 1.2, label: 'Groß' }
                          ]}
                          min={0.8}
                          max={1.2}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                        />
                      </Box>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Animationen
                      </Typography>
                      <FormControlLabel
                        control={<Switch defaultChecked color="primary" />}
                        label="Animationen aktivieren"
                      />
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Sprache und Region
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Sprache</InputLabel>
                        <Select
                          value={currentProfile.language}
                          label="Sprache"
                          onChange={(e) => handleProfileChange('language', e.target.value)}
                        >
                          <MenuItem value="de">Deutsch</MenuItem>
                          <MenuItem value="en">Englisch</MenuItem>
                          <MenuItem value="fr">Französisch</MenuItem>
                          <MenuItem value="es">Spanisch</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Datumsformat</InputLabel>
                        <Select defaultValue="dd.mm.yyyy">
                          <MenuItem value="dd.mm.yyyy">DD.MM.YYYY</MenuItem>
                          <MenuItem value="mm/dd/yyyy">MM/DD/YYYY</MenuItem>
                          <MenuItem value="yyyy-mm-dd">YYYY-MM-DD</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Zeitzone</InputLabel>
                        <Select defaultValue="europe_berlin">
                          <MenuItem value="europe_berlin">Europa/Berlin (GMT+1)</MenuItem>
                          <MenuItem value="europe_london">Europa/London (GMT)</MenuItem>
                          <MenuItem value="america_new_york">Amerika/New York (GMT-5)</MenuItem>
                          <MenuItem value="asia_tokyo">Asien/Tokio (GMT+9)</MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Dashboard-Einstellungen
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Standard-Startseite
                      </Typography>
                      <FormControl fullWidth margin="normal">
                        <Select defaultValue="dashboard">
                          <MenuItem value="dashboard">Dashboard</MenuItem>
                          <MenuItem value="requests">Eingehende Anfragen</MenuItem>
                          <MenuItem value="negotiations">Verhandlungen</MenuItem>
                          <MenuItem value="executions">Ausführungen</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                        Dashboard-Widgets
                      </Typography>
                      <FormControlLabel
                        control={<Switch defaultChecked color="primary" />}
                        label="Eingehende Anfragen"
                      />
                      <FormControlLabel
                        control={<Switch defaultChecked color="primary" />}
                        label="Statistiken"
                      />
                      <FormControlLabel
                        control={<Switch defaultChecked color="primary" />}
                        label="Laufende Analysen"
                      />
                      <FormControlLabel
                        control={<Switch defaultChecked color="primary" />}
                        label="Zahlungsübersicht"
                      />
                      <FormControlLabel
                        control={<Switch color="primary" />}
                        label="Aktivitätsprotokoll"
                      />
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={() => handleSaveSettings('Darstellung')}
                    >
                      Einstellungen speichern
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </Paper>
      </Box>
      
      {/* Dialog zum Hinzufügen eines neuen Teammitglieds */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Neues Teammitglied hinzufügen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Fügen Sie ein neues Mitglied zu Ihrer Organisation hinzu. Der Benutzer erhält eine Einladungs-E-Mail.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="E-Mail-Adresse"
            type="email"
            fullWidth
            variant="outlined"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Rolle</InputLabel>
            <Select defaultValue="viewer">
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="analyst">Analyst</MenuItem>
              <MenuItem value="viewer">Betrachter</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button onClick={handleCloseDialog} variant="contained" color="primary">
            Einladen
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar für Erfolgsmeldungen */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default SettingsPage;
